import { hasRealStore, isRealStore } from "src/lib/stores";

export const GLOBAL_ONBOARDING_SCOPE_KEY = "global";

export const ONBOARDING_NOTICE_KEYS = {
  demoStore: "demo_store_offer_v1",
  manualInputs: "manual_inputs_notice_v1",
  marketingActivity: "marketing_activity_notice_v1",
  marketingMeta: "marketing_meta_notice_v1",
  marketingGoogle: "marketing_google_notice_v1",
};

export const getOnboardingStateId = (scopeKey, key) => `${scopeKey}:${key}`;

const APP_NAME = process.env.APP_NAME || "the app";

const marketingTaskConfigs = [
  {
    key: "connect_meta_ads",
    integrationType: "META_ADS",
    title: "Connect Meta Ads",
  },
  {
    key: "connect_google_ads",
    integrationType: "GOOGLE_ADS",
    title: "Connect Google Ads",
  },
];

const marketingReadinessTypes = new Set([
  "META_ADS",
  "GOOGLE_ADS",
  "TIKTOK_ADS",
]);

const manualInputPageIds = new Set([
  "dashboard",
  "unitEconomics",
  "cohortAnalytics",
  "aovAnalytics",
  "profitBreakeven",
  "normalizedPL",
]);

const getSelectedSourcesCount = (integration) =>
  integration?.settings?.sources?.length || 0;

const hasSelectedSources = (integration) =>
  getSelectedSourcesCount(integration) > 0;

const hasMarketingIntegrationReady = (store) =>
  Boolean(
    store?.children?.some(
      (integration) =>
        marketingReadinessTypes.has(integration.type) &&
        hasSelectedSources(integration)
    )
  );

const getStoreOnboardingSettings = (store) => store?.settings?.onboarding || {};

const createTask = ({
  key,
  title,
  description,
  status,
  tone = "required",
  action = null,
  scopeKey = null,
  canSkip = tone === "recommended" && status !== "blocked",
  skipDescription = "Skipped for now. You can come back to this later.",
}) => ({
  key,
  title,
  description,
  status,
  tone,
  action,
  scopeKey,
  canSkip,
  skipDescription,
  skipped: status === "skipped",
  complete: status === "complete" || status === "skipped",
});

const resolveSkippedTaskState = (task, onboardingStatesById = {}) => {
  if (!task?.canSkip || task.complete || !task.scopeKey) {
    return task;
  }

  const state =
    onboardingStatesById[getOnboardingStateId(task.scopeKey, task.key)];

  if (!state?.completedAt || !state?.payload?.skipped) {
    return task;
  }

  return {
    ...task,
    status: "skipped",
    description: task.skipDescription,
    action: null,
    skipped: true,
    complete: true,
  };
};

/**
 * Builds the checklist task for a single marketing integration.
 *
 * @param {object | null | undefined} store
 * @param {{ key: string, integrationType: string, title: string }} config
 * @param {boolean} isStoreAdmin
 * @returns {ReturnType<typeof createTask>}
 */
const buildMarketingTask = (store, config, isStoreAdmin) => {
  if (!store) {
    return createTask({
      key: config.key,
      title: config.title,
      description: "Connect a store first.",
      status: "blocked",
      tone: "recommended",
      scopeKey: null,
    });
  }

  const integration = store.children?.find(
    ({ type }) => type === config.integrationType
  );

  if (!integration) {
    return createTask({
      key: config.key,
      title: config.title,
      description: isStoreAdmin
        ? `Connect ${config.title.replace("Connect ", "")} to unlock channel-specific marketing insights.`
        : `${config.title.replace("Connect ", "")} is not connected for this store.`,
      status: "action_needed",
      tone: "recommended",
      scopeKey: store.id,
      action: isStoreAdmin
        ? {
            type: "settings",
            tab: "store",
            sectionTarget: "integrations",
            label: "Open settings",
          }
        : null,
    });
  }

  if (!hasSelectedSources(integration)) {
    return createTask({
      key: config.key,
      title: config.title,
      description: isStoreAdmin
        ? "Connection is complete, but sources still need to be selected."
        : "This integration still needs a source selected.",
      status: "action_needed",
      tone: "recommended",
      scopeKey: store.id,
      action: isStoreAdmin
        ? {
            type: "settings",
            tab: "store",
            sectionTarget: "integrations",
            label: "Open settings",
          }
        : null,
    });
  }

  return createTask({
    key: config.key,
    title: config.title,
    description: `Ready with ${getSelectedSourcesCount(integration)} source${getSelectedSourcesCount(integration) === 1 ? "" : "s"}.`,
    status: "complete",
    tone: "recommended",
    scopeKey: store.id,
    action: isStoreAdmin
      ? {
          type: "settings",
          tab: "store",
          sectionTarget: "integrations",
          label: "Open settings",
        }
      : null,
  });
};

const buildInviteTeamTask = ({
  selectedStore,
  currentUserId,
  isStoreAdmin,
}) => {
  if (!selectedStore) {
    return createTask({
      key: "invite_team_members",
      title: "Invite team members",
      description: "Connect a store first.",
      status: "blocked",
      tone: "recommended",
      scopeKey: null,
    });
  }

  const invitedCount = selectedStore.invites?.length || 0;
  const teammateCount =
    selectedStore.users?.filter(
      ({ hidden, user }) => !hidden && user?.id && user.id !== currentUserId
    ).length || 0;
  const totalCollaborators = invitedCount + teammateCount;

  if (totalCollaborators > 0) {
    return createTask({
      key: "invite_team_members",
      title: "Invite team members",
      description: `${totalCollaborators} teammate${totalCollaborators === 1 ? "" : "s"} added or invited for this store.`,
      status: "complete",
      tone: "recommended",
      scopeKey: selectedStore.id,
      action: isStoreAdmin
        ? {
            type: "settings",
            tab: "store",
            sectionTarget: "team",
            label: "Open team settings",
          }
        : null,
    });
  }

  return createTask({
    key: "invite_team_members",
    title: "Invite team members",
    description: isStoreAdmin
      ? "Invite teammates so they can access this store."
      : "A store admin can invite teammates to collaborate.",
    status: "action_needed",
    tone: "recommended",
    scopeKey: selectedStore.id,
    action: isStoreAdmin
      ? {
          type: "settings",
          tab: "store",
          sectionTarget: "team",
          label: "Open team settings",
        }
      : null,
  });
};

const buildPaymentMethodTask = ({
  selectedStore,
  hasPaymentMethod,
  billingLoading = false,
  trialDaysLeft = -1,
}) => {
  const hasTrial = Number.isInteger(trialDaysLeft) && trialDaysLeft >= 0;
  const trialHasEnded = trialDaysLeft === 0;
  const shouldShow =
    !!selectedStore || hasTrial || trialHasEnded || Boolean(hasPaymentMethod);

  if (!shouldShow) {
    return null;
  }

  const tone = trialHasEnded ? "required" : "recommended";

  if (billingLoading) {
    return createTask({
      key: "add_payment_method",
      title: "Add payment method",
      description: "Checking billing setup.",
      status: "in_progress",
      tone,
      scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
      canSkip: false,
    });
  }

  if (hasPaymentMethod) {
    return createTask({
      key: "add_payment_method",
      title: "Add payment method",
      description: "A payment method is on file.",
      status: "complete",
      tone,
      scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
      action: {
        type: "settings",
        tab: "billing",
        label: "Open billing",
      },
      canSkip: false,
    });
  }

  return createTask({
    key: "add_payment_method",
    title: "Add payment method",
    description: trialHasEnded
      ? `Add a payment method to continue using ${APP_NAME}.`
      : hasTrial
        ? `Optional for now. Add a payment method before your trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"}.`
        : "Add a payment method to keep future billing uninterrupted.",
    status: "action_needed",
    tone,
    scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
    action: {
      type: "paywall",
      source: trialHasEnded ? "trialEnded" : "onboarding",
      label: "Add payment method",
    },
    canSkip: !trialHasEnded,
    skipDescription:
      "Skipped for now. Add a payment method before billing becomes required.",
  });
};

/**
 * Derives the current onboarding checklist from integration state, billing
 * state, and persisted user/store onboarding markers.
 *
 * @param {object} params
 * @returns {{
 *   requiredTasks: Array<object>,
 *   recommendedTasks: Array<object>,
 *   tasks: Array<object>,
 *   tasksByKey: Record<string, object>,
 *   completeCount: number,
 *   totalCount: number,
 *   progressPercent: number,
 *   isManualInputsReviewed: boolean,
 *   manualInputsReviewedAt: string | null | undefined,
 *   hasAnyMarketingReady: boolean,
 * }}
 */
export const buildOnboardingState = ({
  selectedStore,
  stores = [],
  slackIntegration,
  onboardingStatesById = {},
  currentUserId = null,
  hasPaymentMethod = false,
  billingLoading = false,
  trialDaysLeft = -1,
  isStoreAdmin = false,
  demoStoreStatus = null,
}) => {
  const isDemoSelected = selectedStore?.isDemo === true;
  const hasAnyRealStore = hasRealStore(stores) || isRealStore(selectedStore);
  const selectedRealStore = isDemoSelected ? null : selectedStore;
  const isDemoOnly = isDemoSelected && !hasAnyRealStore;
  const onboardingSettings = getStoreOnboardingSettings(selectedRealStore);
  const manualInputsReviewedAt = onboardingSettings.manualInputsReviewedAt;
  const slackChannelsCount = getSelectedSourcesCount(slackIntegration);
  const paymentMethodTask = isDemoOnly
    ? null
    : buildPaymentMethodTask({
        selectedStore: selectedRealStore,
        hasPaymentMethod,
        billingLoading,
        trialDaysLeft,
      });
  const isPaymentRequired = paymentMethodTask?.tone === "required";
  const connectStoreAction = !hasAnyRealStore
    ? {
        type: "shopify_listing",
        label: "Add store",
      }
    : selectedRealStore
      ? {
          type: "settings",
          tab: "store",
          sectionTarget: "store_name_input",
          label: "Open store settings",
        }
      : null;

  const requiredTasks = [
    createTask({
      key: "connect_shopify_store",
      title: "Connect Shopify store",
      description: hasAnyRealStore
        ? "A Shopify store is connected."
        : "Connect your first store to start syncing data.",
      status: hasAnyRealStore ? "complete" : "action_needed",
      scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
      action: connectStoreAction,
    }),
    ...(isPaymentRequired && paymentMethodTask ? [paymentMethodTask] : []),
  ].map((task) => resolveSkippedTaskState(task, onboardingStatesById));

  const recommendedTasks = [
    ...(!demoStoreStatus?.joined
      ? [
          createTask({
            key: ONBOARDING_NOTICE_KEYS.demoStore,
            title: "Try the demo store",
            description: demoStoreStatus?.dismissed
              ? "Skipped for now. You can try the demo store later."
              : "Explore a demo workspace before connecting your own store.",
            status: demoStoreStatus?.dismissed ? "skipped" : "action_needed",
            tone: "recommended",
            scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
            action: demoStoreStatus?.dismissed
              ? null
              : {
                  type: "demo_store",
                  label: "Try demo",
                },
            skipDescription:
              "Skipped for now. You can try the demo store later.",
          }),
        ]
      : []),
    ...(!isDemoSelected
      ? [
          createTask({
            key: "review_manual_inputs",
            title: "Review manual inputs",
            description: manualInputsReviewedAt
              ? "Manual inputs have been reviewed for this store."
              : "Open the manual inputs and review fixed, variable, and marketing costs for this store.",
            status: !selectedRealStore
              ? "blocked"
              : manualInputsReviewedAt
                ? "complete"
                : "action_needed",
            tone: "recommended",
            scopeKey: selectedRealStore?.id || null,
            action: selectedRealStore
              ? {
                  type: "settings",
                  tab: "store",
                  sectionTarget: "manual_inputs",
                  label: "Review inputs",
                }
              : null,
          }),
          ...marketingTaskConfigs.map((config) =>
            buildMarketingTask(selectedRealStore, config, isStoreAdmin)
          ),
          buildInviteTeamTask({
            selectedStore: selectedRealStore,
            currentUserId,
            isStoreAdmin,
          }),
        ]
      : []),
    ...(!isPaymentRequired && paymentMethodTask ? [paymentMethodTask] : []),
    createTask({
      key: "connect_slack",
      title: "Connect Slack",
      description: slackChannelsCount
        ? `Slack is ready with ${slackChannelsCount} channel${slackChannelsCount === 1 ? "" : "s"}.`
        : slackIntegration
          ? "Slack is connected, but no channels are selected yet."
          : "Connect Slack to push reports and alerts.",
      status: slackChannelsCount
        ? "complete"
        : slackIntegration
          ? "action_needed"
          : "action_needed",
      tone: "recommended",
      scopeKey: GLOBAL_ONBOARDING_SCOPE_KEY,
      action: {
        type: "settings",
        tab: "account",
        sectionTarget: "slack",
        label: slackIntegration ? "Open Slack settings" : "Connect Slack",
      },
    }),
  ]
    .filter(Boolean)
    .map((task) => resolveSkippedTaskState(task, onboardingStatesById));

  const tasks = [...requiredTasks, ...recommendedTasks];
  const completeCount = tasks.filter((task) => task.complete).length;
  const tasksByKey = Object.fromEntries(tasks.map((task) => [task.key, task]));

  return {
    requiredTasks,
    recommendedTasks,
    tasks,
    tasksByKey,
    completeCount,
    totalCount: tasks.length,
    progressPercent: tasks.length
      ? Math.round((completeCount / tasks.length) * 100)
      : 0,
    isManualInputsReviewed: Boolean(manualInputsReviewedAt),
    manualInputsReviewedAt,
    hasAnyMarketingReady: hasMarketingIntegrationReady(selectedRealStore),
    hasRealStore: hasAnyRealStore,
    isDemoSelected,
  };
};

/**
 * Resolves an inline onboarding notice for the current analytics/settings page.
 *
 * @param {object} params
 * @returns {object | null}
 */
export const resolveOnboardingPageNotice = ({
  pageId,
  selectedStore,
  onboarding,
  dismissedNoticeIds = new Set(),
  isStoreAdmin = false,
}) => {
  if (!selectedStore || selectedStore.isDemo || !pageId || !onboarding) {
    return null;
  }

  if (
    manualInputPageIds.has(pageId) &&
    !onboarding.tasksByKey?.review_manual_inputs?.complete &&
    !dismissedNoticeIds.has(
      `${selectedStore.id}:${ONBOARDING_NOTICE_KEYS.manualInputs}`
    )
  ) {
    return {
      key: ONBOARDING_NOTICE_KEYS.manualInputs,
      scopeKey: selectedStore.id,
      title: "Review manual inputs",
      description: `${APP_NAME} can only be finance-grade once your fixed, variable, and other marketing costs are reviewed for this store.`,
      action: {
        type: "settings",
        tab: "store",
        sectionTarget: "manual_inputs",
        label: "Open manual inputs",
      },
    };
  }

  if (
    pageId === "marketingActivity" &&
    ["connect_meta_ads", "connect_google_ads"].some(
      (key) => !onboarding.tasksByKey?.[key]?.complete
    ) &&
    !dismissedNoticeIds.has(
      `${selectedStore.id}:${ONBOARDING_NOTICE_KEYS.marketingActivity}`
    )
  ) {
    return {
      key: ONBOARDING_NOTICE_KEYS.marketingActivity,
      scopeKey: selectedStore.id,
      title: "Connect marketing integrations",
      description: isStoreAdmin
        ? "Connect your marketing integrations and choose sources to populate marketing activity."
        : "Marketing integrations still need to be connected and configured for this store.",
      action: isStoreAdmin
        ? {
            type: "settings",
            tab: "store",
            sectionTarget: "integrations",
            label: "Open integrations",
          }
        : null,
    };
  }

  if (pageId === "meta") {
    const metaTask = onboarding.recommendedTasks.find(
      ({ key }) => key === "connect_meta_ads"
    );

    if (metaTask && !metaTask.complete) {
      if (
        dismissedNoticeIds.has(
          `${selectedStore.id}:${ONBOARDING_NOTICE_KEYS.marketingMeta}`
        )
      ) {
        return null;
      }

      return {
        key: ONBOARDING_NOTICE_KEYS.marketingMeta,
        scopeKey: selectedStore.id,
        title: "Finish Meta Ads setup",
        description: metaTask.description,
        action: metaTask.action,
      };
    }
  }

  if (pageId === "google") {
    const googleTask = onboarding.recommendedTasks.find(
      ({ key }) => key === "connect_google_ads"
    );

    if (googleTask && !googleTask.complete) {
      if (
        dismissedNoticeIds.has(
          `${selectedStore.id}:${ONBOARDING_NOTICE_KEYS.marketingGoogle}`
        )
      ) {
        return null;
      }

      return {
        key: ONBOARDING_NOTICE_KEYS.marketingGoogle,
        scopeKey: selectedStore.id,
        title: "Finish Google Ads setup",
        description: googleTask.description,
        action: googleTask.action,
      };
    }
  }

  return null;
};
